/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable, Injector } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { isNil, head, find, isEmpty } from 'lodash';
import { DataSource, SitesService, CompanyUtilService } from '@shared/services';
import {
  ENTITY_TYPES,
  IBaseFormService,
  TRANSFER_TYPE,
  ILimit,
  IFilterQuery,
  IApiListResponse,
  IEvent,
  ITransferGroupFormFieldPath,
  ObjectId,
} from '@shared/types';
import { lazyInject } from '@app/containers/base/decorators';
import {
  getFormFieldValue,
  setFormFieldValue,
  getFormControl,
} from '@app/components/form/services/schema-form/schema-form-functions';
import { TransferBaseFormService } from '../transfer-common';
import { BehaviorSubject } from 'rxjs';
import {
  getSubtractDateString,
  toDate,
  toMoment,
} from '@app/shared/utils/date-utils';
import { ONLY_DATE_SUBSTRING_LENGTH } from '@config/global-constants';
import { rx } from '@app/shared/rx';
import {
  composeWithDefaultEventQuery,
  getPreEventTransactionDateRange,
  getTransferEventQueryFilter,
} from '../transfer/transfer.query.util';
import { TransferGroupAutoRestockService } from './transfer-group-auto-restock.service';
import { TransferAutoRestockFormService } from '../transfer-auto-restock/transfer-auto-restock.form.service';
import { getObservableValue } from '@app/shared/utils/common-utils';

@Injectable()
export class TransferGroupAutoRestockAddFormService
  extends TransferBaseFormService
  implements IBaseFormService<ENTITY_TYPES.TRANSFER_GROUP>
{
  @lazyInject(CompanyUtilService) public companyService: CompanyUtilService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(TransferGroupAutoRestockService)
  public transferGroupAutoRestockService: TransferGroupAutoRestockService;
  @lazyInject(TransferAutoRestockFormService)
  public transferAutoRestockFormService: TransferAutoRestockFormService;

  public formFieldPath: ITransferGroupFormFieldPath = {
    transferGroupType: 'transfer_group_type',
    transferType: 'transfer_type',
    eventId: 'event_id',
    transactionDate: 'transaction_date',
    name: 'name',
    virtualTransactionDate: 'virtual.transaction_date',
  };

  public eventIdDataSource: DataSource;
  public selectedEvent: IEvent;
  public transactionDateLimit: ILimit<string | null> = {
    minimumObs: new BehaviorSubject(
      getSubtractDateString(toDate().toISOString(), ONLY_DATE_SUBSTRING_LENGTH),
    ),
    maximumObs: new BehaviorSubject(null),
  };

  public eventList: IEvent[] = [];

  public constructor(public injector: Injector) {
    super(injector);
    this.initEventDataSource();
  }

  public initEventDataSource(): void {
    this.eventIdDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.EVENTS,
    );
    this.eventIdDataSource.fetchList = this.eventDataSourceFetchList.bind(this);
  }

  public onFormReady(form: FormGroup): void {
    super.onFormReady(form);
    this.eventIdDataSource?.refresh();
  }

  public async initDefaultEvent(): Promise<void> {
    const eventValue = getFormFieldValue(this.form, this.formFieldPath.eventId);
    const autoPopulateEventId = this.getAutoPopulateForNewData({
      targetFieldPath: this.formFieldPath.eventId,
    });
    const isHaveAutoPopulateEventId = !isEmpty(autoPopulateEventId);
    if (isEmpty(eventValue) || isHaveAutoPopulateEventId) {
      const eventList = (await this.eventIdDataSource.nextPage()) as IEvent[];
      this.selectedEvent = isHaveAutoPopulateEventId
        ? (find(eventList, { id: autoPopulateEventId }) as IEvent)
        : this.transferAutoRestockFormService.getMostRecentEventAfterNow(
            eventList,
          );

      setFormFieldValue(
        this.form,
        this.formFieldPath.eventId,
        this.selectedEvent?.id,
        false,
      );
    }

    const salesStartDateTimeUTC = head(
      this.selectedEvent?.event_days,
    )?.start_date_time;
    const companyCurrentDate = this.companyService.getCurrentTime();
    const salesStartDate = this.companyService.convertUTCDateWithTimeZone(
      salesStartDateTimeUTC,
      true,
    );
    const { minimum, maximum } = getPreEventTransactionDateRange(
      companyCurrentDate,
      salesStartDate,
    );

    this.transactionDateLimit.minimumObs.next(minimum);
    this.transactionDateLimit.maximumObs.next(maximum);
  }

  public onEventChange(selectedEvent: IEvent): void {
    this.getFormFieldControl(this.formFieldPath.transactionDate).reset();
    this.getFormFieldControl(this.formFieldPath.virtualTransactionDate).reset();

    const salesStartDateTimeUTC = head(
      selectedEvent.event_days,
    )?.start_date_time;
    const companyCurrentDate = this.companyService.getCurrentTime();
    const salesStartDate = this.companyService.convertUTCDateWithTimeZone(
      salesStartDateTimeUTC,
      true,
    );

    if (this.isPreEventTypeTransfer()) {
      const { minimum, maximum } = getPreEventTransactionDateRange(
        companyCurrentDate,
        salesStartDate,
      );

      this.transactionDateLimit.minimumObs.next(minimum);
      this.transactionDateLimit.maximumObs.next(maximum);
      setFormFieldValue(
        this.form,
        this.formFieldPath.transactionDate,
        companyCurrentDate,
      );
    }
  }

  public eventDataSourceFetchList(
    query: IFilterQuery,
  ): Promise<IApiListResponse> {
    const transferTypeValue: TRANSFER_TYPE = getFormFieldValue(
      this.form,
      this.formFieldPath.transferType,
    );

    const userQuery = {
      filter: getTransferEventQueryFilter(transferTypeValue),
      orderby: 'event_days/start_date_time asc',
    };
    const eventQuery = composeWithDefaultEventQuery(userQuery);

    return this.eventIdDataSource.service
      .fetchList(eventQuery)
      .pipe(
        rx.first(),
        rx.map((response) => {
          this.eventList = response.items as IEvent[];

          return response;
        }),
      )
      .toPromise()
      .then(
        (result) => result,
        (error) => (
          this.eventIdDataSource.error.next(error), Promise.reject(error)
        ),
      );
  }

  public initDefaultTransferDate(): void {
    const dateValue = getFormFieldValue(
      this.form,
      this.formFieldPath.transactionDate,
    );
    const autoPopulateDate = this.getAutoPopulateForNewData<string>({
      targetFieldPath: this.formFieldPath.transactionDate,
    });
    let transactionDate = null;

    if (!isEmpty(autoPopulateDate)) {
      const minDate = getObservableValue(this.transactionDateLimit.minimumObs);
      const maxDate = getObservableValue(this.transactionDateLimit.maximumObs);

      if (
        toMoment(autoPopulateDate).isSameOrAfter(toMoment(minDate)) &&
        toMoment(autoPopulateDate).isSameOrBefore(toMoment(maxDate))
      ) {
        transactionDate = autoPopulateDate;
      }
    } else if (isNil(dateValue) || isEmpty(dateValue)) {
      transactionDate = this.companyService.getCurrentTime();
    }

    if (!isNil(transactionDate)) {
      setFormFieldValue(
        this.form,
        this.formFieldPath.transactionDate,
        transactionDate,
        false,
      );
    }
  }

  public popWindowFormReady(): Function {
    return async (form: FormGroup): Promise<void> => {
      this.form = form;

      this.eventIdDataSource?.refresh();
      await this.initDefaultEvent();
      this.initDefaultTransferDate();

      getFormControl(form, this.formFieldPath.eventId)
        .valueChanges.pipe(
          rx.filter(() => {
            const isTouched = getFormControl(
              form,
              this.formFieldPath.eventId,
            )?.touched;

            return isTouched;
          }),
          rx.distinctUntilChanged(),
        )
        .subscribe((eventId: ObjectId) => {
          if (!isNil(eventId)) {
            const selectedEvent: IEvent = find(this.eventList, {
              id: eventId,
            });
            if (!isNil(selectedEvent)) {
              this.selectedEvent = selectedEvent;
              this.onEventChange(selectedEvent);
            }
          }
        });
    };
  }
}
